import React, { useEffect, useRef } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import EmptyState from '../../../ui/EmptyState';
import Skeleton from '../../../ui/Skeleton';
import { apiClient } from '../../../api/apiClient';
import { usePlaytest, playtestActions } from '../../../context/PlaytestContext';
import { useTheme } from '../../../context/ThemeContext';
import PlaytestBoardPage from '../components/PlaytestBoardPage';

const POLL_INTERVAL_MS = 2000;

/** 403/404: kein Teilnehmer bzw. Session weg — eine Wiederholung ändert daran nichts. */
const isFinalError = (error) => error?.status === 403 || error?.status === 404;

/**
 * Route `/playtest/board/:sessionId`: lädt die Session einmal per HTTP, initialisiert den
 * Playtest-Zustand und zeigt dann `PlaytestBoardPage`; ab dort ist der Socket die einzige Quelle.
 *
 * - Polling nur, solange die Session nicht `active` ist — danach würde jede Antwort den
 *   Socket-Zustand überschreiben (fremde Hände kommen per HTTP leer). Nach einem Fehler pollt
 *   nichts mehr (der Fehler-Zustand bietet „erneut versuchen"); 403/404 werden nicht wiederholt.
 * - Eine Session in der Lobby (`waiting`) gehört auf die Lobby-Seite.
 * - Laden: `Skeleton` auf der Brettfläche; Fehler: `EmptyState` mit Weg zum Einstieg, bei
 *   vorübergehenden Fehlern zusätzlich „erneut versuchen".
 * - Das Brett ist immer dunkel: `setThemeOverride('dark')` beim Betreten, `clearThemeOverride`
 *   beim Verlassen; App-Theme und gespeicherte Wahl bleiben unberührt.
 */
export default function PlaytestBoardRoute() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { state, dispatch } = usePlaytest();
  const { setThemeOverride, clearThemeOverride } = useTheme();
  const initializedForRef = useRef(null);

  useEffect(() => {
    setThemeOverride('dark');
    return () => clearThemeOverride();
  }, [setThemeOverride, clearThemeOverride]);

  const { data: session, error, refetch, isFetching } = useQuery({
    queryKey: ['playtestSession', sessionId],
    queryFn: async () => (await apiClient.get(`/api/playtest/${sessionId}`)).data,
    enabled: !!sessionId,
    refetchInterval: (query) => (query?.state?.error || query?.state?.data?.status === 'active' ? false : POLL_INTERVAL_MS),
    staleTime: Infinity,
    retry: (failureCount, err) => !isFinalError(err) && failureCount < 2,
  });

  // Je Session genau einmal: alter Zustand eines früheren Bretts fliegt, danach füllt der Socket.
  useEffect(() => {
    if (session?.status !== 'active' || initializedForRef.current === sessionId) return;
    initializedForRef.current = sessionId;
    dispatch({ type: 'RESET' });
    dispatch(playtestActions.initializeSession({
      sessionId: session.sessionId,
      hostId: session.hostId ?? null,
      players: session.players || [],
      status: session.status,
      currentUserId: session.currentUserId,
    }));
  }, [session, sessionId, dispatch]);

  if (error) {
    const gone = isFinalError(error);
    const title = error.status === 403 ? 'Du bist kein Teilnehmer dieses Playtests'
      : error.status === 404 ? 'Diesen Playtest gibt es nicht mehr'
        : 'Verbindung unterbrochen';
    return (
      <div className="playtest-board playtest-board--loading">
        <EmptyState
          variant="error"
          icon={AlertTriangle}
          title={title}
          onRetry={gone || isFetching ? undefined : () => refetch()}
          action={<button type="button" className="btn-primary" onClick={() => navigate('/playtest')}>Zum Einstieg</button>}
        />
      </div>
    );
  }

  if (session?.status === 'waiting') {
    return <Navigate to={`/playtest/lobby/${sessionId}`} replace />;
  }

  const ready = session?.status === 'active' && String(state.sessionId) === String(session.sessionId);
  if (!ready) {
    return (
      <div className="playtest-board playtest-board--loading" data-testid="playtest-board-loading">
        <Skeleton width="min(480px, 80vw)" height="48px" radius="lg" label="Playtest wird geladen" />
        <Skeleton width="min(720px, 90vw)" height="40vh" radius="lg" />
        <Skeleton width="min(560px, 85vw)" height="96px" radius="lg" />
      </div>
    );
  }

  return <PlaytestBoardPage sessionId={sessionId} />;
}
